import {
    getSuccessMessages,
    getWarningMessages,
    getInfoMessages,
    getErrorMessagesFromCode,
    getErrorMsgFromStatus,
    getSpecificOPErrorMessages,
    getGenericServerErrorMsg
} from './messageUtils';
import { showToast } from './toastUtils';

/**
 * Resolves error messages based on resultCode, errorObject, or operation/parameter.
 */
function getErrorMessage({ t, operation, parameter, resultCode, errorObject, isNormal }) {
    if (typeof resultCode === 'number' && resultCode > -1) {
        return getErrorMessagesFromCode(t, resultCode, isNormal);
    }
    if (errorObject?.status || typeof errorObject?.toString === 'function') {
        return getErrorMsgFromStatus(t, errorObject, isNormal);
    }
    if (operation || parameter) {
        return getSpecificOPErrorMessages(t, operation, parameter, resultCode);
    }
    return getGenericServerErrorMsg(t);
}

/**
 * Generates a toastId based on available context to prevent duplicates.
 */
function generateToastId({ type, operation, parameter, resultCode, errorObject, useToastId }) {
    if(useToastId) {
        if (typeof resultCode === 'number' && resultCode > -1) {
            return `${type}-code-${resultCode}`;
        }
        if (typeof errorObject?.status === 'number') {
            return `${type}-status-${errorObject.status}`;
        }
        if (operation && typeof parameter === 'string') {
            return `${type}-${operation}-${parameter}`;
        }
        if (operation) {
            return `${type}-${operation}`;
        }
    }
    return `${type}-generic-${Date.now()}`;
}

function isInfoParam(param) {
    return param && typeof param.name === 'string' && typeof param.value === 'string';
}

/**
 * Displays a toast notification with a localized or direct message.
 *
 * @param {Object} options
 * @param {'success'|'warning'|'info'|'error'} [options.type='info'] - Toast type.
 * @param {Function} options.t - Translation function.
 * @param {string} [options.operation] - Translation key suffix (e.g. 'saveSelection').
 * @param {string|{name: string, value: string}} [options.parameter] -
 *     For 'info' type: object with `name` and `value` for interpolation.
 *     For other types: string used as translation key suffix.
 * @param {Object} [options.errorObject] - Error object (e.g. caught exception or HTTP error).
 * @param {number} [options.resultCode] - Numeric error code for structured resolution.
 * @param {string} [options.message] - Raw message to display directly (bypasses resolution).
 * @param {boolean} [options.inSupport=false] - Flag to route toast to support container.
 * @param {boolean} [options.isNormal=true] - Controls alternate phrasing for certain errors.
 * @param {string} [options.position='top-center'] - Toast position.
 * @param {number} [options.autoClose=15000] - Duration in milliseconds before toast auto-dismisses.
 * @param {boolean} [options.useToastId=false] - Attempt to generate a toastid.
 */
export const notifyWithMessage = ({
                                      type = 'info',
                                      t,
                                      operation,
                                      parameter,
                                      errorObject,
                                      resultCode,
                                      message,
                                      inSupport = false,
                                      isNormal = true,
                                      position = 'top-center',
                                      autoClose = 15000,
                                      useToastId = false
                                  }) => {
    let resolvedMessage = '';

    if (message) {
        resolvedMessage = message;
    } else {
        switch (type) {
            case 'success':
                resolvedMessage = getSuccessMessages(t, operation, parameter);
                break;
            case 'warning':
                resolvedMessage = getWarningMessages(t, operation, parameter);
                break;
            case 'info':
                if (isInfoParam(parameter)) {
                    resolvedMessage = getInfoMessages(t, operation, parameter.name, parameter.value);
                } else {
                    resolvedMessage = getInfoMessages(t, operation);
                }
                break;
            case 'error':
                resolvedMessage = getErrorMessage({ t, operation, parameter, resultCode, errorObject, isNormal });
                break;
            default:
                console.warn(`notifyWithMessage: Unknown type "${type}"`);
        }
    }

    if (!resolvedMessage) {
        console.warn(`notifyWithMessage: No message generated for type "${type}"`);
    }

    const toastId = generateToastId({ type, operation, parameter, resultCode, errorObject, useToastId });

    showToast({
        level: type,
        message: resolvedMessage,
        inSupport,
        toastId,
        position,
        autoClose
    });
};